import { getToken } from '@luminovo/auth';
import { isPresent } from '@luminovo/commons';
import {
    EndpointRegistry,
    extractEndpointsToBeInvalidated,
    extractEndpointsToBeRemoved,
    http,
    HttpOptions,
    RegisteredHttpEndpoint,
    uploadFileToBlobStorage,
} from '@luminovo/http-client';
import { useMutation, UseMutationOptions, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import * as z from 'zod';
import { useCurrentUserDetailsContext } from '../../components/contexts/CurrentUserDetailsContext';
import { useDebugErrorHandler } from '../http/debugErrorHandler';
import { httpQueryKey } from '../http/httpQueryKey';

type HttpOptionsWithFiles<T extends RegisteredHttpEndpoint> = HttpOptions<T> & { files: Array<File> | File };

type Metadata = {
    /*
     * The user id who uploaded the file.
     */
    uploaded_by: string;
};

export function useAzureBlobMetadata(): Metadata {
    const { user } = useCurrentUserDetailsContext();
    return {
        uploaded_by: user.id,
    };
}

/**
 * A custom hook for making type-safe HTTP mutation requests with file uploads
 */
export function useHttpFileUpload<T extends RegisteredHttpEndpoint>(
    endpoint: T,
    getUrl: (response: z.infer<EndpointRegistry[T]['responseBody']>) => string,
    options: {
        snackbarMessage: string | null;
        onSuccess?: UseMutationOptions<
            z.infer<EndpointRegistry[T]['responseBody']>,
            unknown,
            HttpOptionsWithFiles<T>,
            unknown
        >['onSuccess'];
        onError?: UseMutationOptions<
            z.infer<EndpointRegistry[T]['responseBody']>,
            unknown,
            HttpOptionsWithFiles<T>,
            unknown
        >['onError'];
        /**
         * When set to true we automaticaly invalidate and remove all inactive queries.
         *
         * @default true
         */
        enableInvalidateAllQueries?: boolean;
        /**
         * If true, multiple files can be uploaded at once.
         * @default true
         */
        multiple?: boolean;
        /**
         * Maximum number of files that can be uploaded at once.
         */
        maxFiles?: number;

        /**
         * Callback function to update the progress of the upload.
         * @param progress - The progress of the upload as a number between 0 and 1.
         */
        onProgress?: (progress: number | undefined) => void;
    },
): UseMutationResult<z.infer<EndpointRegistry[T]['responseBody']>, unknown, HttpOptionsWithFiles<T>, unknown> {
    const {
        snackbarMessage,
        onSuccess = () => {},
        onError: userOnError,
        enableInvalidateAllQueries = true,
        multiple = true,
        maxFiles = Infinity,
    } = options;
    const snackbarKey = 'upload-progress';
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const defaultOnError = useDebugErrorHandler();
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: httpQueryKey(endpoint),
        mutationFn: async (variables: HttpOptionsWithFiles<T>) => {
            const files = toFileArray(variables.files);

            const progressByFile = new Map<File, { loadedBytes: number; totalBytes: number }>();

            const onProgress = ({
                file,
                loadedBytes,
                totalBytes,
            }: {
                file: File;
                loadedBytes: number;
                totalBytes: number;
            }) => {
                progressByFile.set(file, { loadedBytes, totalBytes });

                const progress =
                    Array.from(progressByFile.values()).reduce(
                        (acc, curr) => acc + curr.loadedBytes / curr.totalBytes,
                        0,
                    ) / progressByFile.size;

                if (progress > 0.99) {
                    // reset progress to undefined
                    options.onProgress?.(undefined);
                } else {
                    options.onProgress?.(progress);
                }
            };

            const response = await http(endpoint, variables, getToken());

            if (files.length > maxFiles) {
                throw new Error(`Too many files uploaded. Maximum number of files is ${maxFiles}`);
            }
            if (!multiple && files.length > 1) {
                throw new Error('Only one file can be uploaded at once');
            }

            return await Promise.all(
                files.map((file) => uploadFileToBlobStorage(file, getUrl(response), { onProgress })),
            );
        },
        onSettled: () => {
            closeSnackbar(snackbarKey);
        },
        onSuccess: async (data, variables, context) => {
            await queryClient.removeQueries({ type: 'inactive' });
            if (enableInvalidateAllQueries) {
                await queryClient.invalidateQueries();
            }

            await Promise.allSettled(
                extractEndpointsToBeRemoved(endpoint).map((endpoint) =>
                    queryClient.removeQueries({
                        queryKey: [endpoint],
                    }),
                ),
            );

            await Promise.allSettled(
                extractEndpointsToBeInvalidated(endpoint).map((endpoint) =>
                    queryClient.invalidateQueries({
                        queryKey: [endpoint],
                    }),
                ),
            );

            if (isPresent(snackbarMessage)) {
                enqueueSnackbar(snackbarMessage, {
                    variant: 'success',
                    anchorOrigin: { vertical: 'top', horizontal: 'center' },
                    autoHideDuration: 2000,
                });
            }

            await onSuccess(data, variables, context);
        },
        onError: (error, variables, context) => {
            userOnError?.(error, variables, context);
            defaultOnError(error);
        },
    });
}

function toFileArray(files: File | File[]): File[] {
    return Array.isArray(files) ? files : [files];
}

export interface BaseFile {
    url: string;
    name: string;
}

export interface PendingUploadFile extends BaseFile {
    file: File;
    state: 'PendingUpload';
}

export interface RemovedFile extends BaseFile {
    file: File;
    state: 'RemovedLocal';
}

export interface StoredInAzureFile extends BaseFile {
    file: null;
    state: 'StoredInAzure';
}

export interface PendingRemovalFile extends BaseFile {
    file: null;
    state: 'PendingRemoval';
}

export type AttachmentFile = PendingUploadFile | RemovedFile | StoredInAzureFile | PendingRemovalFile;

export function useAttachmentFileMutation<T>({
    mutateUpload,
    mutateDelete,
    mutateDuplicate,
    onSuccess,
    snackbarMessage,
}: {
    mutateUpload: (file: PendingUploadFile, context: T) => Promise<unknown>;
    mutateDelete?: (file: PendingRemovalFile, context: T) => Promise<unknown>;
    mutateDuplicate?: (file: StoredInAzureFile, context: T) => Promise<unknown>;
    onSuccess?: () => void;
    snackbarMessage: string | null;
}) {
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: async ({ files, context }: { files: AttachmentFile[]; context: T }) => {
            const promises = files.map(async (file) => {
                switch (file.state) {
                    case 'PendingUpload':
                        return await mutateUpload(file, context);
                    case 'PendingRemoval':
                        return await mutateDelete?.(file, context);
                    case 'StoredInAzure':
                        return await mutateDuplicate?.(file, context);
                    case 'RemovedLocal':
                        return; /** no-op */
                }
            });

            await Promise.all(promises);
        },
        onSuccess: () => {
            if (snackbarMessage) {
                enqueueSnackbar(snackbarMessage, { variant: 'success' });
            }
            onSuccess?.();
        },
    });
}
